/*
 * <gift-quiz> Christmas gift quiz. Walks through the details, "who is it
 * for", question and budget steps, scores the answers against the result
 * blocks, shows the winning spirit with the budget-matched product picked
 * first, and (with consent) signs the shopper up via the Shopify customer
 * form with tags describing their result.
 */
if (!customElements.get('gift-quiz')) {
  customElements.define(
    'gift-quiz',
    class GiftQuiz extends HTMLElement {
      connectedCallback() {
        this.card = this.querySelector('.gift-quiz__card');
        this.steps = [...this.querySelectorAll('[data-step]')];
        this.progress = this.querySelector('.gift-quiz__progress');
        this.progressBar = this.querySelector('.gift-quiz__progress-bar');
        this.signupForm = this.querySelector('.gift-quiz__signup');
        this.results = [...this.querySelectorAll('.gift-quiz__result')];

        // Remember the original copy of anything with {tokens} so it can be re-personalised.
        this.querySelectorAll('[data-self], [data-gift], .gift-quiz__question span').forEach((el) => {
          if (el.textContent.includes('{')) el.dataset.template = el.textContent;
        });

        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('submit', (event) => this.onSubmit(event));

        this.reset();
      }

      reset() {
        this.state = { firstName: '', email: '', consent: false, mode: 'self', recipient: '', budget: '', result: '' };
        this.signedUp = false;
        this.steps.forEach((step) => {
          if (step.tagName === 'FORM') step.reset();
          step.querySelectorAll('input[type="radio"]').forEach((input) => (input.checked = false));
        });
        this.querySelector('.gift-quiz__recipient').hidden = true;
        this.show(0, false);
      }

      // Keep the shopper's details and go again, e.g. for the next person on their list.
      restart() {
        this.state = { ...this.state, mode: 'self', recipient: '', budget: '', result: '' };
        this.signedUp = false;
        this.steps
          .filter((step) => step.dataset.step !== 'details')
          .forEach((step) => {
            if (step.tagName === 'FORM') step.reset();
            step.querySelectorAll('input[type="radio"]').forEach((input) => (input.checked = false));
          });
        this.querySelector('.gift-quiz__recipient').hidden = true;
        this.show(this.steps.findIndex((step) => step.dataset.step === 'who'));
      }

      onClick(event) {
        const target = event.target.closest('button');
        if (!target) return;

        if (target.matches('[data-next]')) this.show(this.index + 1);
        else if (target.matches('[data-back]')) this.show(this.index - 1);
        else if (target.matches('[data-restart]')) this.restart();
        else if (target.matches('.gift-quiz__product-add')) this.addToCart(target);
        else if (target.matches('.gift-quiz__share')) this.share();
      }

      onChange(event) {
        const input = event.target;
        const step = input.closest('[data-step]');
        if (!step || input.type !== 'radio') return;

        if (step.dataset.step === 'who') {
          const recipient = this.querySelector('.gift-quiz__recipient');
          recipient.hidden = input.value !== 'gift';
          if (!recipient.hidden) recipient.querySelector('input').focus();
          return;
        }

        if (step.dataset.step === 'question' || step.dataset.step === 'budget') {
          // Brief pause so the shopper sees their pick highlight before moving on.
          clearTimeout(this.advanceTimer);
          this.advanceTimer = setTimeout(() => {
            if (step.dataset.step === 'budget') {
              this.state.budget = input.value;
              this.showResult();
            } else {
              this.show(this.index + 1);
            }
          }, 250);
        }
      }

      onSubmit(event) {
        event.preventDefault();
        const form = event.target;
        const error = form.querySelector('.gift-quiz__error');
        error.hidden = true;

        if (form.dataset.step === 'details') {
          const name = form.elements.first_name;
          const email = form.elements.email;
          if (!name.value.trim()) return this.fail(error, 'Please tell us your first name.', name);
          if (!email.value.trim() || !email.checkValidity()) return this.fail(error, 'Please enter a valid email address.', email);

          this.state.firstName = name.value.trim();
          this.state.email = email.value.trim();
          this.state.consent = form.elements.consent.checked;
        }

        if (form.dataset.step === 'who') {
          const mode = form.querySelector('input[name="mode"]:checked');
          if (!mode) return this.fail(error, 'Choose who the spirit is for.');

          this.state.mode = mode.value;
          this.state.recipient = mode.value === 'gift' ? form.elements.recipient.value.trim() : '';
          this.personalise();
        }

        this.show(this.index + 1);
      }

      fail(error, message, field) {
        error.textContent = message;
        error.hidden = false;
        if (field) field.focus();
      }

      show(index, focus = true) {
        this.index = Math.max(0, Math.min(index, this.steps.length - 1));
        this.steps.forEach((step, i) => (step.hidden = i !== this.index));

        const current = this.steps[this.index];
        const isEnd = this.index === 0 || current.dataset.step === 'result';
        this.progress.hidden = isEnd;
        this.progressBar.style.width = `${(this.index / (this.steps.length - 1)) * 100}%`;

        if (!focus) return;
        const heading = current.querySelector('.gift-quiz__question, .gift-quiz__result:not([hidden]) .gift-quiz__result-name');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        if (this.card.getBoundingClientRect().top < 0) this.card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      personalise() {
        const gift = this.state.mode === 'gift';
        const recipient = this.state.recipient;
        const tokens = {
          '{me}': this.state.firstName || 'So',
          '{name}': recipient || 'them',
          "{name's}": recipient ? `${recipient}'s` : 'their',
        };

        this.querySelectorAll('[data-self]').forEach((el) => (el.hidden = gift));
        this.querySelectorAll('[data-gift]').forEach((el) => (el.hidden = !gift));
        this.querySelectorAll('[data-template]').forEach((el) => {
          const text = Object.entries(tokens).reduce((copy, [token, value]) => copy.split(token).join(value), el.dataset.template);
          el.textContent = text.charAt(0).toUpperCase() + text.slice(1);
        });
      }

      winningResult() {
        const scores = new Map(this.results.map((result) => [result.dataset.resultKey, 0]));

        this.steps
          .filter((step) => step.dataset.step === 'question')
          .forEach((step) => {
            const checked = step.querySelector('input:checked');
            if (!checked) return;
            checked.value
              .split(',')
              .map((key) => key.trim().toLowerCase())
              .filter((key) => scores.has(key))
              .forEach((key) => scores.set(key, scores.get(key) + 1));
          });

        // Ties go to the result block listed first.
        let winner = this.results[0];
        this.results.forEach((result) => {
          if (scores.get(result.dataset.resultKey) > scores.get(winner.dataset.resultKey)) winner = result;
        });
        return winner;
      }

      showResult() {
        const winner = this.winningResult();
        if (!winner) return;
        this.state.result = winner.dataset.resultKey;

        this.results.forEach((result) => (result.hidden = result !== winner));

        const products = [...winner.querySelectorAll('.gift-quiz__product')];
        const pick = products.find((product) => product.dataset.format === this.state.budget) || products[0];
        products.forEach((product) => {
          const isPick = product === pick;
          product.classList.toggle('is-pick', isPick);
          product.querySelector('.gift-quiz__product-badge').hidden = !isPick;
        });

        const share = this.querySelector('.gift-quiz__share');
        share.hidden = !navigator.share || this.state.mode === 'gift';

        this.querySelector('.gift-quiz__step--result .gift-quiz__error').hidden = true;
        this.show(this.steps.findIndex((step) => step.dataset.step === 'result'));
        this.signUp();
      }

      tags() {
        return [
          this.dataset.tag,
          `gift-quiz-${this.state.mode}`,
          `spirit-${this.state.result}`,
          `budget-${this.state.budget}`,
        ].filter(Boolean);
      }

      signUp() {
        if (!this.state.consent || this.signedUp || !this.signupForm) return;
        this.signedUp = true;

        const form = this.signupForm;
        form.elements['contact[email]'].value = this.state.email;
        form.elements['contact[first_name]'].value = this.state.firstName;
        form.elements['contact[tags]'].value = this.tags().join(', ');
        fetch(form.action, { method: 'POST', body: new FormData(form) }).catch(() => {});

        const properties = {
          result: this.state.result,
          result_name: this.querySelector('.gift-quiz__result:not([hidden]) .gift-quiz__result-name')?.textContent.trim(),
          for: this.state.mode === 'gift' ? 'someone else' : 'themselves',
          recipient_name: this.state.recipient,
          budget: this.state.budget,
        };
        try {
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
            window.klaviyo.identify({ email: this.state.email, first_name: this.state.firstName });
            window.klaviyo.track('Completed Christmas Gift Quiz', properties);
          } else if (window._learnq) {
            window._learnq.push(['identify', { $email: this.state.email, $first_name: this.state.firstName }]);
            window._learnq.push(['track', 'Completed Christmas Gift Quiz', properties]);
          }
        } catch (error) {
          // Klaviyo is optional; the Shopify sign-up above still runs.
        }
      }

      addToCart(button) {
        const items = [{ id: Number(button.dataset.variantId), quantity: 1 }];
        const giftBag = this.querySelector('.gift-quiz__gift-bag input');
        if (this.state.mode === 'gift' && this.dataset.giftBagId && giftBag && giftBag.checked) {
          items.push({ id: Number(this.dataset.giftBagId), quantity: 1 });
        }

        const cartDrawer = document.querySelector('cart-drawer');
        const error = this.querySelector('.gift-quiz__step--result .gift-quiz__error');
        const label = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding…';
        error.hidden = true;

        const body = JSON.stringify({
          items,
          sections: cartDrawer ? ['cart-drawer', 'cart-icon-bubble'] : [],
          sections_url: window.location.pathname,
        });

        fetch(window.routes.cart_add_url, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((state) => {
            if (state.status) throw new Error(state.description || state.message);
            if (cartDrawer) {
              cartDrawer.renderContents(state);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch((err) => {
            error.textContent = err.message || 'Sorry, that could not be added. Please try again.';
            error.hidden = false;
          })
          .finally(() => {
            button.textContent = label;
            button.disabled = false;
          });
      }

      share() {
        const name = this.querySelector('.gift-quiz__result:not([hidden]) .gift-quiz__result-name')?.textContent.trim();
        navigator
          .share({
            title: 'My WhataHoot spirit',
            text: `My WhataHoot spirit is ${name}! What's yours?`,
            url: window.location.href.split('#')[0],
          })
          .catch(() => {});
      }
    }
  );
}
